import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import LoadingButton from 'components/common/LoadingButton';
import IconButton from 'components/common/IconButton';
import {
  useJuzProgress,
  useRecordJuzProgress,
  useDeleteJuzProgress
} from 'hooks/useHafizProgress';

export const JUZ_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

const RecordProgressModal = ({ show, onHide, orphanId, branchId, current }) => {
  const { recordProgress, isRecording } = useRecordJuzProgress();
  const [juz, setJuz] = useState(
    current?.juz ? Math.min(30, current.juz + 1) : 1
  );
  const [recordedOn, setRecordedOn] = useState(dayjs().format('YYYY-MM-DD'));
  const [notes, setNotes] = useState('');

  const handleSave = () => {
    recordProgress(
      { orphanId, branchId, juz: Number(juz), recordedOn, notes },
      { onSuccess: onHide }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Record Juz progress</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={6}>
            <Form.Label>Juz now being memorised *</Form.Label>
            <Form.Select value={juz} onChange={e => setJuz(e.target.value)}>
              {JUZ_OPTIONS.map(n => (
                <option key={n} value={n}>
                  Juz {n}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6}>
            <Form.Label>Date *</Form.Label>
            <Form.Control
              type="date"
              value={recordedOn}
              onChange={e => setRecordedOn(e.target.value)}
            />
          </Col>
          <Col xs={12}>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Completed Juz 3 revision, strong recitation"
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isRecording}
          disabled={isRecording || !recordedOn}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

RecordProgressModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  orphanId: PropTypes.string.isRequired,
  branchId: PropTypes.string,
  current: PropTypes.object
};

/**
 * Hafiz Quran memorisation progress: the Juz the child is currently on and
 * the full history beneath. Shown on the profile for Hafiz-type children.
 */
const HafizProgressCard = ({ orphanId, branchId }) => {
  const { history, current, isLoading } = useJuzProgress(orphanId);
  const { deleteEntry } = useDeleteJuzProgress();
  const [showRecord, setShowRecord] = useState(false);

  return (
    <Card className="border shadow-none mb-3">
      <Card.Header className="pb-0">
        <Flex justifyContent="between" alignItems="center" className="pt-2">
          <Card.Title as="h5" className="mb-0">
            <FontAwesomeIcon icon="book" className="me-2" />
            Hafiz Quran Progress
          </Card.Title>
          <IconButton
            icon="plus"
            size="sm"
            variant="falcon-primary"
            onClick={() => setShowRecord(true)}
          >
            Record progress
          </IconButton>
        </Flex>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <p className="text-600 mb-0">Loading…</p>
        ) : current ? (
          <Flex alignItems="center" className="mb-3 flex-wrap gap-3">
            <div className="text-center border rounded p-3">
              <p className="text-600 fs--2 mb-1">Current Juz</p>
              <h2 className="text-primary mb-0">{current.juz}</h2>
              <p className="text-500 fs--2 mb-0">of 30</p>
            </div>
            <div>
              <p className="mb-1">
                Reached on{' '}
                <strong>
                  {dayjs(current.recorded_on).format('DD MMM YYYY')}
                </strong>
              </p>
              <div
                className="progress"
                style={{ height: '10px', minWidth: 220 }}
                title={`${Math.round((current.juz / 30) * 100)}% of the Quran`}
              >
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(current.juz / 30) * 100}%` }}
                />
              </div>
              {current.notes && (
                <p className="text-600 fs--1 mb-0 mt-1">{current.notes}</p>
              )}
            </div>
          </Flex>
        ) : (
          <p className="text-600 mb-3">No progress recorded yet.</p>
        )}

        {history.length > 0 && (
          <>
            <h6 className="text-700 mb-2">History</h6>
            <Table responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Juz</th>
                  <th>Notes</th>
                  <th>Recorded by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((row, i) => (
                  <tr
                    key={row.id}
                    className={i === 0 ? 'fw-medium' : 'text-600'}
                  >
                    <td>{dayjs(row.recorded_on).format('DD MMM YYYY')}</td>
                    <td>
                      <SoftBadge bg={i === 0 ? 'primary' : 'secondary'} pill>
                        Juz {row.juz}
                      </SoftBadge>
                    </td>
                    <td>{row.notes || '—'}</td>
                    <td className="fs--1">
                      {[
                        row.recorded_by_user?.first_name,
                        row.recorded_by_user?.last_name
                      ]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => {
                          if (window.confirm('Remove this progress entry?'))
                            deleteEntry(row.id);
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
      {showRecord && (
        <RecordProgressModal
          show={showRecord}
          onHide={() => setShowRecord(false)}
          orphanId={orphanId}
          branchId={branchId}
          current={current}
        />
      )}
    </Card>
  );
};

HafizProgressCard.propTypes = {
  orphanId: PropTypes.string.isRequired,
  branchId: PropTypes.string
};

export default HafizProgressCard;
